import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  Table, 
  Space, 
  Tag, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Tooltip,
  Typography,
  Card,
  Row,
  Col,
  message,
  Spin
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  EyeOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;

const AuditoriaDrawer = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [auditoria, setAuditoria] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  
  // Filtros
  const [filtros, setFiltros] = useState({
    search: '',
    accion: '',
    usuario_id: '',
    tabla_afectada: '',
    fecha_inicio: null,
    fecha_fin: null
  });

  const [usuarios, setUsuarios] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  
  const { api } = useAuth();

  // Cargar datos iniciales
  useEffect(() => {
    if (visible) {
      cargarAuditoria();
      cargarUsuarios();
    }
  }, [visible]);

  // Cargar auditoría cuando cambien filtros o paginación
  useEffect(() => {
    if (visible) {
      cargarAuditoria();
    }
  }, [filtros, pagination.current, pagination.pageSize]);

  const cargarUsuarios = async () => {
    try {
      const response = await api.get('/api/usuarios-admin', {
        params: { limit: 1000 }
      });
      setUsuarios(response.data?.usuarios || []);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    }
  };

  const cargarAuditoria = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filtros
      };

      // Formatear fechas
      if (filtros.fecha_inicio) {
        params.fecha_inicio = dayjs(filtros.fecha_inicio).format('YYYY-MM-DD');
      }
      if (filtros.fecha_fin) {
        params.fecha_fin = dayjs(filtros.fecha_fin).format('YYYY-MM-DD');
      }

      const response = await api.get('/api/usuarios-admin/auditoria', { params });
      
      setAuditoria(response.data?.auditoria || []);
      setPagination(prev => ({
        ...prev,
        total: response.data?.total || 0
      }));
    } catch (error) {
      console.error('Error cargando auditoría:', error);
      message.error('Error al cargar la auditoría');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (key, value) => {
    setFiltros(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      search: '',
      accion: '',
      usuario_id: '',
      tabla_afectada: '',
      fecha_inicio: null,
      fecha_fin: null
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getAccionColor = (accion) => {
    const colorMap = {
      'LOGIN_SUCCESS': 'green',
      'LOGIN_FAILED': 'red',
      'LOGOUT': 'blue',
      'CREATE_USER': 'cyan',
      'UPDATE_USER': 'orange',
      'DELETE_USER': 'volcano',
      'PASSWORD_CHANGE': 'purple',
      'PASSWORD_RESET': 'magenta'
    };
    return colorMap[accion] || 'default';
  };

  const formatearDatos = (datos) => {
    if (!datos) return 'N/A';
    
    try {
      const parsed = typeof datos === 'string' ? JSON.parse(datos) : datos;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return datos.toString();
    }
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'fecha_accion',
      key: 'fecha_accion',
      width: 160,
      render: (fecha) => (
        <div>
          <div>{dayjs(fecha).format('DD/MM/YYYY')}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(fecha).format('HH:mm:ss')}
          </Text>
        </div>
      ),
      sorter: true
    },
    {
      title: 'Usuario',
      dataIndex: 'usuario_nombre',
      key: 'usuario',
      width: 120,
      render: (nombre, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{nombre || 'Sistema'}</div>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              ID: {record.usuario_id || 'N/A'}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Acción',
      dataIndex: 'accion',
      key: 'accion',
      width: 130,
      render: (accion) => (
        <Tag color={getAccionColor(accion)} icon={<ClockCircleOutlined />}>
          {accion.replace(/_/g, ' ')}
        </Tag>
      )
    },
    {
      title: 'Tabla',
      dataIndex: 'tabla_afectada',
      key: 'tabla_afectada',
      width: 150,
      render: (tabla) => (
        <Tag icon={<DatabaseOutlined />} color="blue">
          {tabla}
        </Tag>
      )
    },
    {
      title: 'Registro ID',
      dataIndex: 'registro_id',
      key: 'registro_id',
      width: 100,
      align: 'center'
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120,
      render: (ip) => (
        <Text code style={{ fontSize: '11px' }}>
          {ip}
        </Text>
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="Ver detalles">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedRecord(record);
              setDetailVisible(true);
            }}
          />
        </Tooltip>
      )
    }
  ];

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination(newPagination);
  };

  return (
    <>
      <Drawer
        title={
          <Space>
            <DatabaseOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Auditoría del Sistema
            </Title>
          </Space>
        }
        width={1200}
        open={visible}
        onClose={onClose}
        destroyOnClose
      >
        {/* Filtros */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Buscar..."
                prefix={<SearchOutlined />}
                value={filtros.search}
                onChange={(e) => handleFiltroChange('search', e.target.value)}
                allowClear
              />
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filtrar por acción"
                value={filtros.accion}
                onChange={(value) => handleFiltroChange('accion', value)}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value="LOGIN_SUCCESS">Login Exitoso</Option>
                <Option value="LOGIN_FAILED">Login Fallido</Option>
                <Option value="LOGOUT">Logout</Option>
                <Option value="CREATE_USER">Crear Usuario</Option>
                <Option value="UPDATE_USER">Actualizar Usuario</Option>
                <Option value="DELETE_USER">Eliminar Usuario</Option>
                <Option value="PASSWORD_CHANGE">Cambio de Contraseña</Option>
                <Option value="PASSWORD_RESET">Reset de Contraseña</Option>
              </Select>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filtrar por usuario"
                value={filtros.usuario_id}
                onChange={(value) => handleFiltroChange('usuario_id', value)}
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {usuarios.map(usuario => (
                  <Option key={usuario.id} value={usuario.id}>
                    {usuario.nombre_persona} ({usuario.usuario})
                  </Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                placeholder={['Fecha inicio', 'Fecha fin']}
                value={[filtros.fecha_inicio, filtros.fecha_fin]}
                onChange={(dates) => {
                  handleFiltroChange('fecha_inicio', dates?.[0]);
                  handleFiltroChange('fecha_fin', dates?.[1]);
                }}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
          
          <Row style={{ marginTop: 16 }}>
            <Col>
              <Space>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={cargarAuditoria}
                >
                  Recargar
                </Button>
                <Button onClick={limpiarFiltros}>
                  Limpiar Filtros
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={auditoria}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total: ${total} registros`
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
        />
      </Drawer>

      {/* Modal de detalles */}
      <Drawer
        title="Detalles de Auditoría"
        width={600}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
        destroyOnClose
      >
        {selectedRecord && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card title="Información General">
              <Row gutter={[16, 8]}>
                <Col span={8}><Text strong>ID:</Text></Col>
                <Col span={16}>{selectedRecord.id}</Col>
                
                <Col span={8}><Text strong>Fecha:</Text></Col>
                <Col span={16}>{dayjs(selectedRecord.fecha_accion).format('DD/MM/YYYY HH:mm:ss')}</Col>
                
                <Col span={8}><Text strong>Usuario:</Text></Col>
                <Col span={16}>{selectedRecord.usuario_nombre || 'Sistema'}</Col>
                
                <Col span={8}><Text strong>Acción:</Text></Col>
                <Col span={16}>
                  <Tag color={getAccionColor(selectedRecord.accion)}>
                    {selectedRecord.accion}
                  </Tag>
                </Col>
                
                <Col span={8}><Text strong>Tabla:</Text></Col>
                <Col span={16}>{selectedRecord.tabla_afectada}</Col>
                
                <Col span={8}><Text strong>Registro ID:</Text></Col>
                <Col span={16}>{selectedRecord.registro_id || 'N/A'}</Col>
                
                <Col span={8}><Text strong>IP:</Text></Col>
                <Col span={16}>
                  <Text code>{selectedRecord.ip_address}</Text>
                </Col>
                
                <Col span={8}><Text strong>User Agent:</Text></Col>
                <Col span={16}>
                  <Text style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                    {selectedRecord.user_agent}
                  </Text>
                </Col>
              </Row>
            </Card>

            {selectedRecord.datos_anteriores && (
              <Card title="Datos Anteriores">
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  overflow: 'auto'
                }}>
                  {formatearDatos(selectedRecord.datos_anteriores)}
                </pre>
              </Card>
            )}

            {selectedRecord.datos_nuevos && (
              <Card title="Datos Nuevos">
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  overflow: 'auto'
                }}>
                  {formatearDatos(selectedRecord.datos_nuevos)}
                </pre>
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </>
  );
};

export default AuditoriaDrawer;